import { useEffect, useState } from "react";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { Radar, Smartphone, Globe, TrendingUp, MapPin, Users, DollarSign } from "lucide-react";
import LiveFeed from "@/components/LiveFeed";
import { PlanUpgradeNotice, isPlanUpgradeError } from "@/components/merchant/PlanUpgradeNotice";

function Bar({ label, count, max, testid }) {
  const pct = max > 0 ? Math.max(4, (count / max) * 100) : 4;
  return (
    <div data-testid={testid} className="mb-2">
      <div className="flex justify-between text-xs text-slate-600 mb-0.5">
        <span className="truncate max-w-[70%]">{label}</span>
        <span className="font-mono">{count}</span>
      </div>
      <div className="h-2 rounded-full bg-white/60 overflow-hidden">
        <div className="h-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function MerchantTracking() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [walletSurface, setWalletSurface] = useState(null);
  const [planLock, setPlanLock] = useState(null);

  useEffect(() => {
    api.get(`/merchant/analytics/tracking?days=${days}`).then((r) => setData(r.data))
      .catch((e) => { if (isPlanUpgradeError(e)) setPlanLock(e.response.data.detail); });
  }, [days]);
  useEffect(() => {
    api.get(`/merchant/analytics/wallet-surface?days=${days}`)
      .then((r) => setWalletSurface(r.data))
      .catch(() => setWalletSurface(null));
  }, [days]);

  if (planLock) return <PlanUpgradeNotice detail={planLock} testid="tracking-plan-locked" />;
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;
  const buckets = [
    { title: "Traffic source", icon: Radar, rows: data.by_traffic_source, prefix: "ts" },
    { title: "Device type", icon: Smartphone, rows: data.by_device, prefix: "dev" },
    { title: "Browser", icon: Globe, rows: data.by_browser, prefix: "brw" },
    { title: "UTM source", icon: TrendingUp, rows: data.by_utm_source, prefix: "utm-src" },
    { title: "UTM campaign", icon: TrendingUp, rows: data.by_utm_campaign, prefix: "utm-cmp" },
    { title: "Country", icon: MapPin, rows: data.by_country, prefix: "country" },
    { title: "Province / region", icon: MapPin, rows: data.by_province, prefix: "province" },
  ];

  return (
    <div data-testid="merchant-tracking">
      <div className="pf-chip pf-chip-cyan"><Radar className="w-3 h-3" /> Deep analytics</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Tracking &amp; Attribution</h2>
      <p className="text-sm text-slate-600 mt-1">Every click, every referrer, every device — sliced across UTM parameters and campaigns.</p>

      <div className="flex gap-2 mt-4">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${days === d ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid={`tracking-range-${d}`}>Last {d}d</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
        <div className="pf-card p-4" data-testid="tracking-kpi-clicks">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Clicks</div>
          <div className="font-display text-2xl mt-1 text-slate-900">{data.total_clicks}</div>
        </div>
        <div className="pf-card p-4" data-testid="tracking-kpi-visitors">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Users className="w-3 h-3" /> Visitors</div>
          <div className="font-display text-2xl mt-1 text-slate-900">{data.unique_visitors ?? 0}</div>
        </div>
        <div className="pf-card p-4" data-testid="tracking-kpi-sessions">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sessions</div>
          <div className="font-display text-2xl mt-1 text-slate-900">{data.unique_sessions ?? 0}</div>
        </div>
        <div className="pf-card p-4" data-testid="tracking-kpi-conversions">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Conversions</div>
          <div className="font-display text-2xl mt-1 text-emerald-700">{data.conversions ?? 0}</div>
        </div>
        <div className="pf-card p-4" data-testid="tracking-kpi-revenue">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><DollarSign className="w-3 h-3" /> Revenue</div>
          <div className="font-display text-2xl mt-1 text-slate-900">{cad((data.revenue || 0))}</div>
        </div>
        <div className="pf-card p-4" data-testid="tracking-kpi-aov">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Avg order value</div>
          <div className="font-display text-2xl mt-1 text-slate-900">{cad((data.average_order_value || 0))}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {buckets.map((b) => {
          const max = Math.max(1, ...b.rows.map((r) => r.count));
          return (
            <div key={b.title} className="pf-card p-5" data-testid={`bucket-${b.prefix}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
                <b.icon className="w-3 h-3" /> {b.title}
              </div>
              {b.rows.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No data.</div>
              ) : b.rows.map((r, i) => <Bar key={i} label={r.key} count={r.count} max={max} testid={`${b.prefix}-${i}`} />)}
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div className="md:col-span-2">
          <div className="pf-card p-5" data-testid="funnel-by-source">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="w-3 h-3" /> Cohort funnel by traffic source
        </div>
        {(data.funnel_by_source || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No engagement recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="pb-2 pr-4">Source</th>
                  <th className="pb-2 pr-4 text-right">Clicks</th>
                  <th className="pb-2 pr-4 text-right">Claims</th>
                  <th className="pb-2 pr-4 text-right">Redemptions</th>
                  <th className="pb-2 pr-4 text-right">Revenue</th>
                  <th className="pb-2 pr-4 text-right">Overall conv.</th>
                </tr>
              </thead>
              <tbody>
                {data.funnel_by_source.map((f) => (
                  <tr key={f.traffic_source} className="border-t border-white/60" data-testid={`funnel-row-${f.traffic_source}`}>
                    <td className="py-2 pr-4 font-medium text-slate-900 capitalize">{f.traffic_source}</td>
                    <td className="py-2 pr-4 font-mono text-right">{f.clicks}</td>
                    <td className="py-2 pr-4 font-mono text-right">{f.claims}</td>
                    <td className="py-2 pr-4 font-mono text-right text-emerald-700">{f.redemptions}</td>
                    <td className="py-2 pr-4 font-mono text-right">{cad(f.revenue)}</td>
                    <td className="py-2 pr-4 font-mono text-right font-semibold text-cyan-700">{f.overall_conversion != null ? `${f.overall_conversion}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] text-slate-400 mt-3 italic">
              <span className="font-semibold">Overall conversion</span> = redemptions ÷ (clicks + claims).
            </div>
          </div>
        )}
        </div>
        </div>
        <LiveFeed />
      </div>

      <div className="pf-card p-5 mt-4" data-testid="employee-leaderboard">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Employee redemption leaderboard</div>
        {data.by_employee.length === 0 ? (
          <div className="text-xs text-slate-400 italic">Ask cashiers to enter their employee ID on redemption to see who's converting best.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500"><tr><th className="pb-2">Employee</th><th className="pb-2">Redemptions</th><th className="pb-2">Revenue</th></tr></thead>
            <tbody>
              {data.by_employee.map((e, i) => (
                <tr key={e.employee_id + i} className="border-t border-white/60" data-testid={`emp-row-${i}`}>
                  <td className="py-2 font-mono text-xs">{e.employee_id}</td>
                  <td className="py-2 font-mono">{e.redemptions}</td>
                  <td className="py-2 font-mono">{cad(e.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {walletSurface && (
        <div data-testid="cashback-loop-card" className="pf-card mt-6">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            <h3 className="font-display text-lg font-medium tracking-tight text-slate-900">Cashback loop engagement</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Shopper wallet + receipt activity attributed to your storefronts over the last {walletSurface.days} days.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-white/60" data-testid="cashback-loop-wallet-views">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Wallet views</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.wallet_views}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/60" data-testid="cashback-loop-receipt-views">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Receipt views</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.receipt_views}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/60" data-testid="cashback-loop-view-merchant">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">View-merchant taps</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.view_merchant_taps}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/60" data-testid="cashback-loop-report-issue">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Report-issue taps</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.report_issue_taps}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
